<template>
    <main-dialog
        ref="excludeStoresDialog"
        :translated-header="toSentenceCase($tkey(`storeList`, { name: storeGroup.description }))"
        btn-confirm-text="actions.save"
        btn-cancel-text="actions.cancel"
        has-activator
        :confirm-btn-disabled="isSaveDisabled"
        @confirm="saveExcludeStoresList"
        @close="closeDialog"
    >
        <template v-slot:actions>
            <div class="exclude-stores-dialog__activator-btn">
                <v-btn :disabled="disabled" icon density="comfortable" @click.stop="openDialog">
                    <div class="activator" :class="{ excluded: excludedStores.length }" />
                </v-btn>
            </div>
        </template>
        <template v-slot:title>
            <h1 class="exclude-stores-dialog__title">
                {{ toSentenceCase($tkey(`storeList`, { name: storeGroup.description })) }}
            </h1>
        </template>
        <template v-slot:default>
            <div class="exclude-stores-dialog">
                <promo-ag-grid
                    ref="agGrid"
                    :row-data="stores"
                    :column-defs="columnDefs"
                    :grid-options="gridOptions"
                    :default-col-def="defaultColDef"
                    grid-style="width: 115rem; height: 35rem;"
                    grid-class="ag-theme-custom__exclude-stores-dialog"
                    @grid-ready="onReady"
                    @row-data-updated="setIsSaveDisabled"
                    @cell-value-changed="setIsSaveDisabled"
                />
            </div>
        </template>
    </main-dialog>
</template>

<script>
import { markRaw } from 'vue';
import { mapGetters } from 'vuex';
import { map, pick } from 'lodash';
import AgCheckbox from '@/js/components/promo-ag-grid/ag-checkbox.vue';
import PromoAgSelectAllHeader from '@/js/components/promo-ag-grid/ag-select-all-header.vue';
import agGridUtils from '@/js/utils/ag-grid-utils';

export default {
    /* eslint-disable vue/no-unused-components */
    components: {
        AgCheckbox,
        PromoAgSelectAllHeader,
    },
    props: {
        storeGroup: {
            type: Object,
            required: true,
        },
        excludedStores: {
            type: Array,
            default: () => [],
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    localizationKey: 'planning.promotionsMaintenance.resources.excludedStores',
    emits: ['save'],
    data() {
        return {
            defaultColDef: {
                editable: false,
                resizable: false, // Disable column resizing.
                suppressMovable: true, // Stop users from being able to rearrange columns.
                lockPinned: true, // Stop users from being able to pin columns.
                sortable: true, // All columns default to being sortable.
                unSortIcon: true, // Ensures the sort icon displays all the time (not just when hovered over).
                filter: true,
                menuTabs: ['filterMenuTab'],
            },
            gridOptions: markRaw({
                suppressContextMenu: true, // only show filter
                headerHeight: 40,
                rowHeight: 35, // Specified in pixels.
                components: {
                    PromoAgSelectAllHeader,
                    AgCheckbox,
                },
            }),
            isSaveDisabled: false,
            gridApi: null,
        };
    },
    computed: {
        ...mapGetters('storeGroups', ['getStoresByKey']),
        stores() {
            const excludedStoreKeys = map(this.excludedStores, 'key');
            return map(this.getStoresByKey(this.storeGroup.key), store => ({
                ...store,
                // checkbox should be checked if store is include
                // and unchecked if store is exclude
                isIncluded: !excludedStoreKeys.includes(store.key),
            }));
        },
        columnDefs() {
            return [
                ...['clientStoreKey', 'name', 'storeStatus', 'city', 'storeProfile'].map(field => ({
                    headerName: this.toSentenceCase(this.$tkey(`gridHeaders.${field}`)),
                    field,
                    minWidth: 155,
                })),
                ...['openDate', 'closeDate'].map(field => {
                    return {
                        headerName: this.toSentenceCase(this.$tkey(`gridHeaders.${field}`)),
                        filter: 'agDateColumnFilter',
                        filterParams: {
                            // https://www.ag-grid.com/javascript-grid/filter-date/
                            comparator: agGridUtils.agGridDateComparator,
                        },
                        field,
                        minWidth: 150,
                        valueFormatter: params => this.dateShorthand(params.value),
                    };
                }),
                {
                    headerName: '',
                    field: 'isIncluded',
                    headerComponent: 'PromoAgSelectAllHeader',
                    headerComponentParams: { enableSorting: true },
                    cellRenderer: 'AgCheckbox',
                    headerClass: 'flex-cell',
                    cellClass: 'flex-cell',
                    minWidth: 40,
                    sortable: true,
                },
            ];
        },
    },
    methods: {
        onReady(params) {
            this.gridApi = markRaw(params.api);
            this.setIsSaveDisabled();
        },
        getCurrentStores() {
            if (!this.gridApi) {
                return this.stores;
            }

            const currentStores = [];
            this.gridApi.forEachNode(rowNode => {
                if (rowNode.data) {
                    currentStores.push(rowNode.data);
                }
            });

            return currentStores;
        },
        openDialog() {
            this.$refs.excludeStoresDialog.open();
        },
        closeDialog() {
            if (this.$refs.agGrid && this.gridApi) {
                // reset state if user cancel changes
                this.gridApi.forEachNode(rowNode => {
                    const isExcludedStore = this.excludedStores.find(
                        excludedStoreItem => excludedStoreItem.key === rowNode.data.key
                    );
                    rowNode.setDataValue('isIncluded', !isExcludedStore);
                });
            }
            this.$refs.excludeStoresDialog.close();
        },

        saveExcludeStoresList() {
            const excludeStores = this.getCurrentStores()
                .filter(store => !store.isIncluded)
                .map(store => pick(store, ['_id', 'key', 'clientStoreKey', 'name']));
            this.$emit('save', excludeStores);
            this.closeDialog();
        },
        // save button disabled if user exclude all stores
        // We don't use computed because when ag-grid mutate model  this.store
        // recounting of computed property doesn't run
        setIsSaveDisabled() {
            this.isSaveDisabled = !this.getCurrentStores().some(store => store.isIncluded);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';
@import '@style/base/_mixins.scss';

.exclude-stores-dialog {
    .flex-cell {
        @include flex-center;
    }

    :deep(.ag-root-wrapper) {
        min-height: auto;
    }
}

.exclude-stores-dialog__activator-btn {
    .v-btn {
        border-radius: 12px;
    }
}

.activator {
    width: 1.3rem;
    height: 1.3rem;
    border-radius: 50%;
    background: $promo-primary-colour;
    margin-top: 0.1rem;
    cursor: pointer;

    &:hover {
        background: $promo-light-blue;
    }
}
.excluded {
    border: 0.5rem solid $promo-primary-colour;
    background: $promo-white;

    &:hover {
        background: $promo-white;
    }
}

.exclude-stores-dialog__title {
    @include dialog-title;
    text-align: left !important;
}
</style>
